import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiFilter, FiMoreVertical, FiSearch, FiPlus } from 'react-icons/fi';
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../common/Breadcrumbs/Breadcrumbs";
import { getBrandCampaign } from "../../../store/campaignStore/actions/CreateCampaignActions/getBrandCampaignActions";
import DataTable from "react-data-table-component";
import { SidebarImg } from "../../../static/constant/imgConstants";
import { setBrandCampaignCreationData, setBrandCampaignCreationStep } from '../../../store/publicStore/actions/utilsActions/utilsActions';
import Footer from '../../common/Footer/Footer';
import BrandFilterBar from '../../common/BrandFilterBar/BrandFilterBar';

// Helper function for status styling - moved outside components to be accessible to both
const getStatusStyle = (status) => {
    switch (status) {
        case 'APPROVED':
            return 'bg-[#00B69B] text-[#FFFFFF]';
        case 'PENDING_APPROVAL':
            return 'bg-[#FCBE2D] text-[#FFFFFF]';
        case 'REJECTED':
            return 'bg-[#FD5454] text-[#FFFFFF]';
        default:
            return 'bg-[#FCBE2D] text-[#FFFFFF]';
    }
};

// New Campaign Card component for mobile view
const CampaignCard = ({ campaign, onViewDetails }) => (
    <div className="bg-white rounded-lg shadow-md p-4 mb-4 border border-gray-300">
        <div className="flex flex-col space-y-3">
            {/* Campaign Name - Larger Font */}
            <div>
                <span className="text-[1.6rem] font-medium">{campaign.name}</span>
            </div>

            {/* Purpose */}
            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">Purpose:</span>
                <span className="text-[1.4rem]">{campaign.purpose}</span>
            </div>

            {/* Brand Name */}
            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">Brand Name:</span>
                <span className="text-[1.4rem]">{campaign.brandName}</span>
            </div>

            {/* Platform */}
            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">Platform:</span>
                <span className="text-[1.4rem]">{campaign.platform}</span>
            </div>

            {/* Campaign Type */}
            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">Campaign Type:</span>
                <span className="text-[1.4rem]">{campaign.campaignType}</span>
            </div>

            {/* Status */}
            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">Status:</span>
                <span className={`text-[1.4rem] px-2 py-1 rounded-full ${getStatusStyle(campaign.campaignStatus)}`}>
                    {campaign.campaignStatus === "pending_approval" ? "Pending" : campaign.campaignStatus}
                </span>
            </div>

            {/* Start Date */}
            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">Start Date:</span>
                <span className="text-[1.4rem]">{campaign.startDate}</span>
            </div>

            {/* End Date */}
            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">End Date:</span>
                <span className="text-[1.4rem]">{campaign.endDate}</span>
            </div>

            {/* Action Button */}
            <div className="mt-2">
                <button
                    className="bg-blue-500 text-white text-[1.4rem] px-4 py-2 rounded w-full"
                    onClick={() => onViewDetails(campaign)}
                >
                    View Details
                </button>
            </div>
        </div>
    </div>
);

const MyCampaigns = () => {
    const getBrandCampaignReducerData = useSelector(state => state.getBrandCampaignReducer.data);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [searchQuery, setSearchQuery] = useState('');
    const [sorted, setSorted] = useState('');
    const [filteredCampaigns, setFilteredCampaigns] = useState();
    const [isMobile, setIsMobile] = useState(false);
    const [brandId, setBrandId] = useState('');

    const getUserInfoReducerData = useSelector(state => state.getUserInfoReducer.data);

    // Check if the screen is mobile size
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768); // 768px is typical tablet breakpoint
        };

        // Set initial value
        checkIfMobile();

        // Add event listener for window resize
        window.addEventListener('resize', checkIfMobile);

        // Clean up
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    const columns = [
        {
            name: 'Campaign',
            selector: row => row.name,
            sortable: true,
            style: {
                width: '10rem',  // Set a smaller width for this column
            },
            wrap: true,
        },
        {
            name: 'Purpose',
            selector: row => row.purpose,
            sortable: true,
            style: {
                width: '10rem',  // Adjust the width as needed
            },
            wrap: true,
        },
        getUserInfoReducerData?.userType === "AGENCY" &&
        {
            name: 'Brand Name',
            selector: row => row.brandName,
            sortable: true,
            style: {
                width: '10rem',  // Adjust the width as needed
            },
            wrap: true,
        },
        {
            name: 'Platform',
            selector: row => row.platform,
            style: {
                width: '10rem',  // Set a smaller width for this column
            },
        },
        {
            name: 'Campaign Type',
            selector: row => row.campaignType,
            style: {
                width: '10rem',  // Adjust the width for consistency
            },
        },
        {
            name: 'Status',
            selector: row => (
                <span
                    className={`w-full min-h-[2rem] sm:text-sm md:text-[1rem] px-2 rounded-full text-center flex justify-center items-center ${getStatusStyle(row.campaignStatus)}`}
                >
                    <span className="truncate">
                        {row.campaignStatus === "pending_approval" ? "Pending" : row.campaignStatus}
                    </span>
                </span>
            ),
            style: {
                width: '10rem',
            },
        },
        {
            name: 'Start Date',
            selector: row => row.startDate,
            style: {
                width: '10rem',  // Set a smaller width for this column
            },
        },
        {
            name: 'End Date',
            selector: row => row.endDate,
            style: {
                width: '10rem',  // Set a smaller width for this column
            },
        },
        {
            name: 'Action',
            cell: row => <button
                className="bg-blue-500 text-white px-4 py-2 rounded"
                onClick={() => handleViewDetails(row)}
            >
                View Details
            </button>,
            ignoreRowClick: true,
            allowOverflow: true,
            style: {
                width: '10rem',  // Adjust width for the Action column
            },
        },
    ];

    const handleViewDetails = (campaign) => {
        if (getUserInfoReducerData.userType === "AGENCY") {
            navigate('/agency/campaigns/details', { state: { campaign } });
        } else {
            navigate('/brand/campaigns/details', { state: { campaign } });
        }
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleFilter = (brandId) => {
        dispatch(getBrandCampaign({ brandId: brandId ? brandId : "" }));
    };

    const handleReset = () => {
        dispatch(getBrandCampaign({ brandId: "" }));
    };

    useEffect(() => {
        const data = [...(getBrandCampaignReducerData || [])].sort((a, b) => b.campaignId - a.campaignId); // Sort data by campaignId descending
        setSorted(data);
        setFilteredCampaigns(data)
    }, [getBrandCampaignReducerData]);

    useEffect(() => {
        if (searchQuery === '') {
            setFilteredCampaigns(sorted);
        } else {
            const filtered = sorted.filter(campaign =>
                campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                campaign.purpose.toLowerCase().includes(searchQuery.toLowerCase()) ||
                campaign.platform.toLowerCase().includes(searchQuery.toLowerCase()) ||
                campaign.campaignType.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredCampaigns(filtered);
        }
    }, [searchQuery, sorted]);

    const customTableStyles = {
        table: {
            style: {
                position: 'relative',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#F1F4F9',
                minHeight: '52px',
                fontWeight: 700,
                borderRadius: '0.4rem',
                borderBottom: 'none',
                // position: 'fixed',
                top: 0,
            },
            denseStyle: {
                minHeight: '32px',
            },
        },
        rows: {
            style: {
                fontSize: '1.4rem',  // Increased text size for rows
                fontWeight: 600,
                color: '#202224',
                borderBottom: '1px solid #EDEFF1',
                minHeight: '70px',
            },
            denseStyle: {
                minHeight: '40px',
            },
        },
        headCells: {
            style: {
                padding: '1rem',
                fontSize: '1.6rem',
                textAlign: 'left',
            },
        },
        cells: {
            style: {
                padding: '1.5rem',
            },
        },
    };

    useEffect(() => {
        dispatch(getBrandCampaign({ brandId: brandId ? brandId : "" }));
    }, [dispatch]);
    // const handleCreateNewCampaign = () => {
    //     dispatch(setBrandCampaignCreationData({
    //         basicDetails: {
    //             campaignName: null,
    //             campaignPurpose: null,
    //             platform: null,
    //             campaignType: null,
    //             referralCode: null
    //         },
    //         contentDetails: {
    //             startDate: null,
    //             endDate: null,
    //             bannerTemplate: {},
    //             videoPosition: null,
    //             mediaFileLink: null,
    //             mediaFileName: null,
    //             script: null,
    //             dialogue: null,
    //             backgroundColor: null,
    //             otherProperties: null,
    //             isLocationRequired: false,
    //             isBrandApprovalRequired: false,
    //             uploadDuration: null,
    //         },
    //         demographicsDetails: {
    //             region: {},
    //             country: {},
    //             state: {},
    //             campaignAgeGroups: [],
    //             gender: null,
    //             niches: [],
    //             influencerType: null,
    //             targetView: null,
    //             estimatedBudget: null
    //         },
    //     }));
    //     dispatch(setBrandCampaignCreationStep(1));
    //     navigate('/brand/campaigns/create');
    // };

    return (
        <div className="w-full h-full flex flex-col justify-start bg-[#F2F3F7] ">
            <Breadcrumbs breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'My Campaigns', href: getUserInfoReducerData?.userType === "AGENCY" ? "/agency/campaigns" : "/brand/campaigns" }]} />
            <div className="w-full flex justify-between items-center my-[2rem] px-[4rem]">
                <div className="relative py-[1.5rem]">
                    <FiSearch
                        className="absolute top-1/2 left-5 transform -translate-y-1/2 w-[2.1rem] h-[2.1rem] text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        placeholder="Search Campaigns"
                        className="w-[20rem] p-3 border text-[1.4rem] inter-font font-[400] border-gray-300 rounded-[0.3rem] pl-16 outline-none"
                    />
                </div>
                <div className="flex space-x-[1.2rem]">
                    {/*<button className="bg-white p-4 rounded-[1rem] outline-none">*/}
                    {/*    <FiFilter className="inline-block w-[2.1rem] h-[2.1rem]" />*/}
                    {/*</button>*/}
                    {getUserInfoReducerData?.agencyRoleName !== "Viewer" &&
                        <button
                            className="flex items-center bg-[#0D9B50] text-[1.6rem] text-white px-4 py-[1rem] rounded-[0.8rem] outline-none"
                            onClick={() => {
                                dispatch(setBrandCampaignCreationData({
                                    basicDetails: {
                                        campaignName: null,
                                        campaignPurpose: null,
                                        platform: null,
                                        campaignType: null,
                                        referralCode: null,
                                        selectedBrandId: null,
                                    },
                                    contentDetails: {
                                        startDate: null,
                                        endDate: null,
                                        bannerTemplate: {},
                                        videoPosition: null,
                                        mediaFileLink: null,
                                        mediaFileName: null,
                                        script: null,
                                        dialogue: null,
                                        backgroundColor: null,
                                        otherProperties: null,
                                        isLocationRequired: false,
                                        isBrandApprovalRequired: false,
                                        uploadDuration: null,
                                    },
                                    audienceDemographicDetails: {
                                        region: {},
                                        country: {},
                                        state: {},
                                        campaignAgeGroups: [],
                                        gender: null,
                                        niches: [],
                                        influencerType: null,
                                        targetView: null,
                                        estimatedBudget: null,
                                        city: null,
                                    },
                                }));
                                dispatch(setBrandCampaignCreationStep(1));
                                if (getUserInfoReducerData.userType === 'AGENCY') {
                                    navigate('/agency/campaigns/create');
                                } else {
                                    navigate('/brand/campaigns/create');
                                }
                            }}
                        >
                            <img src={SidebarImg.campaigns1} alt="Campaign Icon" className="w-[2.1rem] h-[2.1rem]" />+
                            <h1 className="inter-font font-[500] px-4">New Campaigns</h1>
                        </button>
                    }
                </div>
            </div>
            {getUserInfoReducerData.userType === 'AGENCY' && (
                <div className='w-full h-[12rem] my-2 flex flex-col px-[4rem]'>
                    <BrandFilterBar
                        onFilter={handleFilter}
                        onReset={handleReset}
                    />
                </div>
            )}
            <div className="w-full h-full flex flex-col px-[4rem]">
                <div className="w-full flex flex-col rounded-[2.6rem] bg-[#ffffff]">
                    {/* Sticky Header Section */}
                    <div className="sticky top-0 z-10 bg-[#ffffff] mb-2 p-[3.2rem]">
                        <h1 className="text-[2.4rem] text-[#202224] inter-font font-[700]">
                            Campaigns
                        </h1>
                    </div>

                    {/* Table Container or Card Container based on screen size */}
                    <div className="w-full max-h-[50rem] overflow-y-auto no-scrollbar px-[2.4rem] pb-[2.4rem]">
                        {Array.isArray(filteredCampaigns) && filteredCampaigns.length > 0 ? (
                            isMobile ? (
                                // Mobile view - Cards
                                <div className="flex flex-col space-y-4">
                                    {filteredCampaigns.map((campaign, index) => (
                                        <CampaignCard
                                            key={index}
                                            campaign={campaign}
                                            onViewDetails={handleViewDetails}
                                        />
                                    ))}
                                </div>
                            ) : (
                                // Desktop view - Table
                                <DataTable
                                    columns={columns}
                                    data={filteredCampaigns}
                                    customStyles={customTableStyles}
                                    pagination
                                    highlightOnHover
                                    responsive
                                />
                            )
                        ) : (
                            <div className="text-2xl md:text-4xl leading-[50px] md:leading-[70px] text-gray-500 text-center mt-4">
                                There are no records to display.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default MyCampaigns;
